import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null); // null=loading, false=guest, object=user
    const [loading, setLoading] = useState(true);

    const refresh = useCallback(async () => {
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
        } catch (_e) {
            setUser(false);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const login = async (email, password) => {
        try {
            const { data } = await api.post("/auth/login", { email, password });
            if (data.access_token) localStorage.setItem("foodglow_token", data.access_token);
            setUser(data.user);
            return { ok: true };
        } catch (e) {
            const detail = e.response?.data?.detail;
            if (detail) return { ok: false, error: formatApiErrorDetail(detail) };
            if (!e.response) return { ok: false, error: `Cannot reach server. ${e.message || "Network error"}.` };
            return { ok: false, error: `Server error ${e.response.status}. Please try again.` };
        }
    };

    const register = async (payload) => {
        try {
            const { data } = await api.post("/auth/register", payload);
            if (data.access_token) localStorage.setItem("foodglow_token", data.access_token);
            setUser(data.user);
            return { ok: true };
        } catch (e) {
            const detail = e.response?.data?.detail;
            if (detail) return { ok: false, error: formatApiErrorDetail(detail) };
            if (!e.response) return { ok: false, error: `Cannot reach server. ${e.message || "Network error"}.` };
            return { ok: false, error: `Server error ${e.response.status}. Please try again.` };
        }
    };

    const logout = async () => {
        try {
            await api.post("/auth/logout");
        } catch (_e) {
            /* ignore */
        }
        localStorage.removeItem("foodglow_token");
        setUser(false);
    };

    return (
        <AuthContext.Provider value={{ user, loading, login, register, logout, refresh, setUser }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    return useContext(AuthContext);
}
